/**
 * Module-level canvas registry so any part of the app can capture the
 * current viewer state without coupling to the react-three-fiber hierarchy.
 *
 * Flow:
 *   - ThreeScene registers its WebGL canvas on mount.
 *   - useAutoThumbnailCapture calls captureViewerThumbnail() after a new
 *     checkpoint arrives and uploads the PNG to the checkpoint doc.
 */

let canvasRef: HTMLCanvasElement | null = null;

export function registerViewerCanvas(canvas: HTMLCanvasElement | null): void {
  canvasRef = canvas;
}

/**
 * Capture a resized thumbnail (square crop, centered). Uses an off-screen
 * canvas so we don't mutate the live viewer.
 */
export function captureViewerThumbnail(sidePx = 256): string | null {
  if (!canvasRef) return null;
  const src = canvasRef;
  try {
    const dst = document.createElement('canvas');
    dst.width = sidePx;
    dst.height = sidePx;
    const ctx = dst.getContext('2d');
    if (!ctx) return null;
    const sw = src.width;
    const sh = src.height;
    const side = Math.min(sw, sh);
    const sx = (sw - side) / 2;
    const sy = (sh - side) / 2;
    ctx.fillStyle = '#F0F0F0';
    ctx.fillRect(0, 0, sidePx, sidePx);
    ctx.drawImage(src, sx, sy, side, side, 0, 0, sidePx, sidePx);
    return dst.toDataURL('image/png');
  } catch (e) {
    console.warn('[viewerCapture] thumbnail failed:', e);
    return null;
  }
}
